import { useState, useEffect } from 'react';
import '@styles/MuscleSlider.css';
import "@assets/constants/colours.css";

export const getColorFromId = (id) => {
  const baseHue = 220;
  const variation = (parseInt(id, 36) % 10);
  const saturation = 70 + (variation % 3) * 5;
  const lightness = 50 + (variation * 3);

  return `hsl(${baseHue}, ${saturation}%, ${lightness}%)`;
};

const MuscleSlider = ({ muscles, primaryMuscles, secondaryMuscles, onPrimaryChange, onSecondaryChange }) => {
  // Get CSS variables
  const grey = getComputedStyle(document.documentElement).getPropertyValue('--op_grey');
  const blue = getComputedStyle(document.documentElement).getPropertyValue('--primary');
  const light_grey = getComputedStyle(document.documentElement).getPropertyValue('--light_grey');

  const [primaryInputValues, setPrimaryInputValues] = useState(
    Object.fromEntries(primaryMuscles.map(m => [m.muscle_id, m.percent.toString()]))
  );

  const [secondaryInputValues, setSecondaryInputValues] = useState(
    Object.fromEntries(secondaryMuscles.map(m => [m.muscle_id, m.percent.toString()]))
  );

  useEffect(() => {
    setPrimaryInputValues(
      Object.fromEntries(primaryMuscles.map(m => [m.muscle_id, m.percent.toString()]))
    );
  }, [primaryMuscles]);

  useEffect(() => {
    setSecondaryInputValues(
      Object.fromEntries(secondaryMuscles.map(m => [m.muscle_id, m.percent.toString()]))
    );
  }, [secondaryMuscles]);

  // Combine all muscles for display
  const allMuscles = [...primaryMuscles, ...secondaryMuscles];
  const totalPercentage = 100;

  // Get muscle name and type
 const getMuscleInfo = (id) => {
    const muscle = muscles.find(m => m.id === id);
    const isPrimary = primaryMuscles.some(m => m.muscle_id === id);
    return {
        name: muscle?.name || 'Unknown',
        type: isPrimary ? 'primary' : 'secondary',
        color: getColorFromId(id)
    };
    };


  // Calculate segment positions
  let currentPosition = 0;
  const segments = allMuscles.map(muscle => {
    const segment = {
      id: muscle.muscle_id,
      percent: muscle.percent,
      start: currentPosition,
      end: currentPosition + muscle.percent,
      ...getMuscleInfo(muscle.muscle_id)
    };
    currentPosition += muscle.percent;
    return segment;
  });

  const handleInputChange = (e, muscleId, isPrimary) => {
    const val = e.target.value;
    
    // Update local input state immediately
    if (isPrimary) {
      setPrimaryInputValues(prev => ({
        ...prev,
        [muscleId]: val
      }));
    } else {
      setSecondaryInputValues(prev => ({
        ...prev,
        [muscleId]: val
      }));
    }

    // Only update parent if it's a valid number
    const num = parseFloat(val);
    if (!isNaN(num)) {
      const newPercent = Math.max(0, Math.min(100, parseFloat(num.toFixed(2))));
      if (isPrimary) {
        onPrimaryChange(primaryMuscles.map(m => 
          m.muscle_id === muscleId ? {...m, percent: newPercent} : m
        ));
      } else {
        onSecondaryChange(secondaryMuscles.map(m => 
          m.muscle_id === muscleId ? {...m, percent: newPercent} : m
        ));
      }
    }
  };


  return (
    <div className="muscle-visualization">
      {/* Visualization Bar */}
      <div className="muscle-bar">
        {segments.map(segment => (
          <div
            key={`segment-${segment.id}`}
            className={`muscle-segment ${segment.type}`}
            style={{
              width: `${segment.percent}%`,
              left: `${segment.start}%`,
              backgroundColor: segment.color
            }}
          >
          </div>
        ))}
      </div>

      {/* Primary Muscle Controls */}
      <div className="muscle-controls-group">
        <div className="muscle-controls">
          {primaryMuscles.map(muscle => {
            const muscleInfo = getMuscleInfo(muscle.muscle_id);
            return (
              <div key={`primary-${muscle.muscle_id}`} className="muscle-control">
                <label className="muscle-label">
                    <span
                        className="color-key"
                        style={{ backgroundColor: muscleInfo.color }}
                    />
                    {muscleInfo.name}
                </label>
                <input
                    type="text"
                    inputMode="decimal"
                    value={primaryInputValues[muscle.muscle_id] ?? ""}
                     onChange={(e) => handleInputChange(e, muscle.muscle_id, muscleInfo.type === 'primary')}
                    />
                <span>%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Secondary Muscle Controls */}
      <div className="muscle-controls-group">
        <div className="muscle-controls">
          {secondaryMuscles.map(muscle => {
            const muscleInfo = getMuscleInfo(muscle.muscle_id);
            return (
              <div key={`secondary-${muscle.muscle_id}`} className="muscle-control">
                <label className="muscle-label">
                    <span
                        className="color-key"
                        style={{ backgroundColor: muscleInfo.color }}
                    />
                    {muscleInfo.name}
                </label>
                <input
                    type="text"
                    inputMode="decimal"
                    value={secondaryInputValues[muscle.muscle_id] ?? ""}
                     onChange={(e) => handleInputChange(e, muscle.muscle_id, false)}
                    />
                <span>%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MuscleSlider;